/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { FileUploaderBasic } from "baseui/file-uploader-basic";
import { LuHardDriveUpload } from "react-icons/lu";
import { acceptedFileTypes } from "../../Utils/Common";
import { createUUID } from "../../Utils/Types";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { expandArchives, isArchiveFile } from "../FileArchiveUtils";
import { FileUploadListItem, FileUploadStatus } from "./FileUploadListItem";
import { DexterModal } from "../DexterModal";
import { Block } from "baseui/block";

/**
 * Largest file Dexter accepts, in bytes (10MB). Kept here so the modal can
 * reject oversize files before uploading.
 */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = "10MB";

/** A file the backend accepted, identified for later commit / removal. */
export interface UploadedFile {
  id: string;
  name: string;
  pdfParentId?: string; // For when a file is generated by PDF, stored (but hidden) on backend
}

/**
 * Uploads one file; reports progress and supports cancellation. Resolves with
 * the stored file(s) on success, rejects on failure.
 *
 * Notes on handling PDFs: Resolving with several files models
 * a PDF that the backend parses into its constituent files: the
 * PDF row is then replaced by those generated files.
 */
export type UploadFileFn = (
  file: File,
  opts: { onProgress: (percent: number) => void; signal: AbortSignal }
) => Promise<UploadedFile[]>;

/** Removes a previously uploaded file from the backend by its id. */
export type RemoveFileFn = (fileId: string) => Promise<void>;

/**
 * Ids to remove when rolling back the given uploaded files: each file plus,
 * any one PDF they were parsed from if applicable.
 */
const removableIds = (files: UploadedFile[]): string[] => {
  const ids = new Set<string>();
  files.forEach((file) => {
    ids.add(file.id);
    if (file.pdfParentId) {
      ids.add(file.pdfParentId);
    }
  });
  return [...ids];
};

interface UploadRow {
  id: string;
  fileName: string;
  status: FileUploadStatus;
  progress: number;
  uploaded?: UploadedFile;
  errorMessage?: string;
  controller: AbortController;
}

export interface UploadModalProps {
  /** Whether the upload modal is currently open. */
  isOpen: boolean;

  /** Called when the modal is closed. */
  onClose: () => void;

  /** Header text. Defaults to "Upload". */
  title?: string;

  /** Accept attribute for the file input; also drives archive extraction. */
  accept?: string;

  /** Human-readable list of formats shown under the dropzone. */
  supportedFormats?: string;

  /** Whether the picker/dropzone allows selecting more than one file. */
  multiple?: boolean;

  /** Uploads a single file to Dexter's backend (injected by the caller). */
  uploadFile: UploadFileFn;

  /** Removes an uploaded file from Dexter's backend (injected by the caller). */
  removeFile: RemoveFileFn;

  /**
   * Called with a human-readable message when a dropped file is rejected before
   * it is ever uploaded — an unsupported file type, or one over the size limit.
   * The caller shows a toast. Injected rather than imported so this modal stays
   * presentational and storyable without app context, matching the
   * uploadFile / removeFile.
   */
  onError?: (message: string) => void;

  /**
   * Called with the successfully uploaded files when the user clicks
   * Upload. The Files page refetches. The modal closes afterward.
   *
   * The transform engine canvas additionally creates input nodes from them,
   * but this should be the only place this happens with respect to file uploads.
   */
  onUploaded?: (files: UploadedFile[]) => void;

  /**
   * Render only the dropzone + file rows, without the DexterModal wrapper, so
   * a parent modal can host it under its own
   * header/stepper and own the confirm button.
   *
   * In embedded mode the parent commits by unmounting this component after
   * reading the files from onChange. Closing the parent (isOpen -> false)
   * while it is still mounted rolls back the uploads, as in modal mode.
   */
  embedded?: boolean;

  /**
   * Embedded mode: reports the successfully uploaded files and whether they
   * can be committed (at least one uploaded, none still in flight) every time
   * either changes. Pass a stable callback.
   */
  onChange?: (files: UploadedFile[], canCommit: boolean) => void;

  /**
   * Embedded mode: files already uploaded earlier in the parent's flow (e.g.
   * after pressing Back), shown as successful rows when this mounts.
   */
  initialFiles?: UploadedFile[];

  listMaxHeight?: number;

  /**
   * Shrink the dropzone to a slim "Browse files / or drop to upload" bar once
   * any file row is shown, so the list gets the space. Off by default; it
   * grows back to full size when the list is empty again.
   */
  compactWhenFilesAdded?: boolean;
}

/**
 * A reusable file-upload modal. Its one job is uploading: files are uploaded to
 * Dexter's backend as they are dropped, shown as rows with per-file status, and
 * can be removed with the trash icon; the Upload button commits the uploaded
 * files and closes. Type detection is intentionally NOT done here — it belongs
 * to the file/upload layer and surfaces on the Files table.  (We want the uploader
 * to handle one thing at a time - uploading).
 *
 * The network calls are injected (uploadFile/removeFile) so this component
 * stays reusable on both the Files page, canvas, and wherever else it's needed in Dexter.
 */
export const UploadModal = ({
  isOpen,
  onClose,
  title = "Upload",
  accept = acceptedFileTypes,
  supportedFormats = "XML, Protobuf, JSON, PDF, source code (Java, C/C++, Python, JS), and archives (ZIP, tar.gz)",
  multiple = true,
  uploadFile,
  removeFile,
  onError,
  onUploaded,
  embedded = false,
  onChange,
  initialFiles,
  listMaxHeight,
  compactWhenFilesAdded = false,
}: UploadModalProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const [rows, setRows] = React.useState<UploadRow[]>(() =>
    (initialFiles ?? []).map((file) => ({
      id: createUUID(),
      fileName: file.name,
      status: "success",
      progress: 100,
      uploaded: file,
      controller: new AbortController(),
    }))
  );

  // Latest rows for the close handler, and whether the last close was a commit
  const rowsRef = React.useRef<UploadRow[]>([]);
  rowsRef.current = rows;
  const committedRef = React.useRef(false);

  // Row ids the user cancelled while still uploading.
  // When such an upload finishes, its file only exists on the backend now, so
  // the settle handler removes it there.
  const cancelledIdsRef = React.useRef<Set<string>>(new Set());

  // Dot-prefixed accepted extensions used to filter archive contents.
  const acceptedExtensions = React.useMemo(
    () =>
      accept
        .split(",")
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.startsWith(".")),
    [accept]
  );

  // On close, discard uncommitted work. Remove any files that already
  // reached the backend. Dropping files and closing must not leave orphans on the table.
  React.useEffect(() => {
    if (isOpen) {
      return;
    }
    if (!committedRef.current) {
      const uploaded = rowsRef.current
        .filter((row) => row.uploaded)
        .map((row) => row.uploaded as UploadedFile);
      removableIds(uploaded).forEach((id) => {
        removeFile(id).catch(() => {
          // Best-effort rollback.
        });
      });
      rowsRef.current.forEach((row) => {
        if (!row.uploaded) {
          cancelledIdsRef.current.add(row.id);
        }
      });
    }
    committedRef.current = false;
    setRows([]);
  }, [isOpen, removeFile]);

  const startUpload = React.useCallback(
    (file: File) => {
      const rowId = createUUID();
      const controller = new AbortController();

      setRows((prev) => [
        ...prev,
        {
          id: rowId,
          fileName: file.name,
          status: "uploading",
          progress: 0,
          controller,
        },
      ]);

      uploadFile(file, {
        signal: controller.signal,
        onProgress: (percent) =>
          setRows((prev) =>
            prev.map((row) =>
              row.id === rowId ? { ...row, progress: percent } : row
            )
          ),
      })
        .then((uploaded) => {
          // Cancelled (or the modal closed) while still uploading: the files only
          // exist on the backend now, so remove them (and any parent PDF).
          if (cancelledIdsRef.current.has(rowId)) {
            cancelledIdsRef.current.delete(rowId);
            removableIds(uploaded).forEach((id) => {
              removeFile(id).catch(() => {
                // Best-effort cleanup.
              });
            });
            return;
          }
          // Replace the uploading row with one success row per stored file.
          // a normal file that is a single row; for a PDF it is the files the
          // backend parsed it into, shown in place of the PDF.
          const successRows: UploadRow[] = uploaded.map((storedFile) => ({
            id: createUUID(),
            fileName: storedFile.name,
            status: "success",
            progress: 100,
            uploaded: storedFile,
            controller,
          }));
          setRows((prev) =>
            prev.flatMap((row) => (row.id === rowId ? successRows : [row]))
          );
        })
        .catch((error: unknown) => {
          // A failed upload stored nothing, so there is nothing to remove.
          if (cancelledIdsRef.current.has(rowId)) {
            cancelledIdsRef.current.delete(rowId);
            return;
          }
          const message =
            error instanceof Error ? error.message : "Upload failed.";
          setRows((prev) =>
            prev.map((row) =>
              row.id === rowId
                ? { ...row, status: "error", errorMessage: message }
                : row
            )
          );
        });
    },
    [uploadFile, removeFile]
  );

  const addErrorRow = React.useCallback((fileName: string, message: string) => {
    setRows((prev) => [
      ...prev,
      {
        id: createUUID(),
        fileName,
        status: "error",
        progress: 100,
        errorMessage: message,
        controller: new AbortController(),
      },
    ]);
  }, []);

  // Rejects a file that is over the size limit with a toast, so oversize files
  // never start an upload. Returns whether the file is within the limit.
  const isWithinSizeLimit = React.useCallback(
    (file: File): boolean => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        onError?.(
          `${file.name} exceeds the ${MAX_FILE_SIZE_LABEL} size limit.`
        );
        return false;
      }
      return true;
    },
    [onError]
  );

  const handleFiles = React.useCallback(
    async (files: File[]) => {
      for (const file of files) {
        // Archives are expanded client-side; each contained file gets its own
        // row and upload. The archive itself is never uploaded.
        if (isArchiveFile(file)) {
          try {
            const contained = await expandArchives([file], acceptedExtensions);
            if (contained.length === 0) {
              addErrorRow(file.name, "No supported files found in archive.");
            } else {
              contained.forEach((extracted) => {
                if (isWithinSizeLimit(extracted)) {
                  startUpload(extracted);
                }
              });
            }
          } catch {
            addErrorRow(file.name, `Could not read ${file.name}.`);
          }
        } else if (isWithinSizeLimit(file)) {
          startUpload(file);
        }
      }
    },
    [acceptedExtensions, startUpload, addErrorRow, isWithinSizeLimit]
  );

  const handleCancel = React.useCallback((row: UploadRow) => {
    // Let the upload finish so we can remove the stored file once it lands;
    // aborting could leave an orphan the client never learns about.
    cancelledIdsRef.current.add(row.id);
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  }, []);

  const handleRemove = React.useCallback(
    (row: UploadRow) => {
      // Remove only this file. For a file parsed from a PDF this removes just
      // that one generated file, not its siblings or the PDF.
      if (row.uploaded) {
        removeFile(row.uploaded.id).catch(() => {
          // Best-effort; leave the row removed from the UI regardless.
        });
      }
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    },
    [removeFile]
  );

  // Memoized so onChange only fires when the uploaded set actually changes.
  const uploadedFiles = React.useMemo(
    () =>
      rows
        .filter((row) => row.status === "success" && row.uploaded)
        .map((row) => row.uploaded as UploadedFile),
    [rows]
  );
  const isUploading = rows.some((row) => row.status === "uploading");
  const canCommit = uploadedFiles.length > 0 && !isUploading;

  // When enabled, shrink the dropzone to a slim bar once there are rows. It
  // still accepts drops, and grows back when the list empties.
  const compact = compactWhenFilesAdded && rows.length > 0;

  // Embedded mode: keep the parent's Next button and file list in sync.
  React.useEffect(() => {
    onChange?.(uploadedFiles, canCommit);
  }, [uploadedFiles, canCommit, onChange]);

  const handleCommit = (): void => {
    // Mark this close as a commit so the close effect keeps the uploaded files.
    committedRef.current = true;
    onUploaded?.(uploadedFiles);
    onClose();
  };

  const browseButtonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: currentTheme.spacing.xs,
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
    backgroundColor: currentTheme.colors.background,
    color: currentTheme.colors.defaultTextColor,
    border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: currentTheme.fonts.primary,
    fontSize: currentTheme.fontSizes.medium,
    fontWeight: 600,
  };

  const infoTextStyle: React.CSSProperties = {
    color: currentTheme.colors.secondaryTextColor,
    fontFamily: currentTheme.fonts.primary,
    fontSize: currentTheme.fontSizes.small,
  };

  const content = (
    <>
      <FileUploaderBasic
        accept={accept}
        multiple={multiple}
        onDrop={(acceptedFiles, rejectedFiles) => {
          // react-dropzone puts anything that fails the `accept` filter here;
          // since no maxSize is set on the dropzone, these are type rejections.
          rejectedFiles?.forEach((rejection) => {
            const rejectedFile = rejection as File & { file?: File };
            const rejectedName =
              rejectedFile?.name ?? rejectedFile?.file?.name ?? "A file";
            onError?.(`${rejectedName} is not a supported file type.`);
          });
          if (acceptedFiles.length > 0) {
            handleFiles(acceptedFiles);
          }
        }}
        overrides={{
          Root: { style: { width: "100%" } },
          FileDragAndDrop: {
            style: ({ $isDragActive }) => ({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: currentTheme.spacing.md,
              minHeight: compact ? "0px" : "220px",
              padding: compact
                ? `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`
                : currentTheme.spacing.xl,
              border: `2px dashed ${currentTheme.colors.dropdownBorderColor}`,
              borderRadius: "8px",
              backgroundColor: $isDragActive
                ? currentTheme.colors.tabHoverColor
                : currentTheme.colors.reactFlowBackground,
              textAlign: "center",
            }),
          },
          ContentMessage: { style: { display: "none" } },
          ContentSeparator: { style: { display: "none" } },
          ButtonComponent: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component: ({ onClick }: any) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: currentTheme.spacing.md,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: currentTheme.spacing.md,
                  }}
                >
                  <button
                    type="button"
                    style={browseButtonStyle}
                    onClick={onClick}
                  >
                    <LuHardDriveUpload size={18} /> Browse files
                  </button>
                  <span
                    style={{
                      color: currentTheme.colors.secondaryTextColor,
                      fontFamily: currentTheme.fonts.primary,
                      fontSize: currentTheme.fontSizes.medium,
                    }}
                  >
                    or drop to upload
                  </span>
                </div>
                {!compact && (
                  <>
                    <span style={infoTextStyle}>
                      {`Supported formats: ${supportedFormats}`}
                    </span>
                    <span style={infoTextStyle}>
                      {`Maximum file upload size: ${MAX_FILE_SIZE_LABEL}`}
                    </span>
                  </>
                )}
              </div>
            ),
          },
        }}
      />

      {rows.length > 0 && (
        <Block
          display="flex"
          flexDirection="column"
          marginTop={currentTheme.spacing.md}
          maxHeight={`${listMaxHeight}px`}
          overrides={{
            Block: {
              style: {
                gap: currentTheme.spacing.sm,
                overflowY: "auto",
              },
            },
          }}
        >
          {rows.map((row) => (
            <FileUploadListItem
              key={row.id}
              fileName={row.fileName}
              status={row.status}
              progress={row.progress}
              statusText={row.errorMessage}
              onCancel={() => handleCancel(row)}
              onRemove={() => handleRemove(row)}
            />
          ))}
        </Block>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <DexterModal
      isOpen={isOpen}
      title={title}
      confirmLabel="Upload"
      confirmDisabled={!canCommit}
      onConfirm={handleCommit}
      onCancel={onClose}
    >
      {content}
    </DexterModal>
  );
};
